import { CreatureType, CreatureDefinition, SPAWN_CONFIG } from '../types/GameTypes'

// Creature type definitions
const CREATURE_DEFINITIONS: Record<CreatureType, CreatureDefinition> = {
  rat: {
    type: 'rat',
    name: 'Rat',
    maxHp: 20,
    attack: 5,
    defense: 1,
    experienceReward: 10,
    speed: 80,
    aggroRange: 100,
    attackRange: 30,
    attackSpeed: 1200,
    respawnTime: SPAWN_CONFIG.RESPAWN_TIME,
    color: '#7c6f64',      // brownish gray
    secondaryColor: '#d4c4b7'  // lighter for belly
  },
  snake: {
    type: 'snake',
    name: 'Snake',
    maxHp: 35,
    attack: 12,
    defense: 2,
    experienceReward: 25,
    speed: 100,
    aggroRange: 120,
    attackRange: 35,
    attackSpeed: 1000,
    respawnTime: SPAWN_CONFIG.RESPAWN_TIME,
    color: '#4a7c59',      // green
    secondaryColor: '#8fbc8f'  // lighter green
  },
  wolf: {
    type: 'wolf',
    name: 'Wolf',
    maxHp: 80,
    attack: 25,
    defense: 5,
    experienceReward: 60,
    speed: 140,
    aggroRange: 180,
    attackRange: 35,
    attackSpeed: 900,
    respawnTime: SPAWN_CONFIG.RESPAWN_TIME,
    color: '#505050',      // dark gray
    secondaryColor: '#909090'  // lighter gray
  }
}

export class CreatureFactory {
  /**
   * Get creature definition by type
   */
  public static getDefinition(type: CreatureType): CreatureDefinition {
    return CREATURE_DEFINITIONS[type]
  }

  /**
   * Get all creature types
   */
  public static getAllTypes(): CreatureType[] {
    return Object.keys(CREATURE_DEFINITIONS) as CreatureType[]
  }

  /**
   * Get creature types suitable for a given player level
   */
  public static getTypesForLevel(playerLevel: number): CreatureType[] {
    // All creatures spawn from start, but could filter by level if needed
    if (playerLevel >= 3) {
      return ['rat', 'snake', 'wolf']
    } else if (playerLevel >= 2) {
      return ['rat', 'snake']
    }
    return ['rat']
  }

  /**
   * Get a random creature type weighted by difficulty
   */
  public static getRandomType(zoneX: number, zoneY: number): CreatureType {
    // Distance from town center (tiles 27, 27) influences creature difficulty
    const distFromTown = Math.sqrt(
      Math.pow(zoneX * 10 + 5 - 27, 2) + Math.pow(zoneY * 10 + 5 - 27, 2)
    )

    // Near town: mostly rats
    if (distFromTown < 12) {
      const roll = Math.random()
      if (roll < 0.8) return 'rat'
      if (roll < 0.95) return 'snake'
      return 'wolf'
    }
    // Medium distance: mixed
    else if (distFromTown < 20) {
      const roll = Math.random()
      if (roll < 0.4) return 'rat'
      if (roll < 0.8) return 'snake'
      return 'wolf'
    }
    // Far from town: more dangerous
    else {
      const roll = Math.random()
      if (roll < 0.2) return 'rat'
      if (roll < 0.5) return 'snake'
      return 'wolf'
    }
  }

  /**
   * Generate sprite for a creature type using canvas
   */
  public static generateSprite(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    type: CreatureType,
    frame: number = 0
  ): void {
    const def = CREATURE_DEFINITIONS[type]
    const cx = x + width / 2
    const cy = y + height / 2

    // Animation offset based on frame
    const bobOffset = Math.sin(frame * Math.PI / 2) * 2

    ctx.save()

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.3)'
    ctx.beginPath()
    ctx.ellipse(cx, y + height - 4, width / 3, 4, 0, 0, Math.PI * 2)
    ctx.fill()

    switch (type) {
      case 'rat':
        this.drawRat(ctx, cx, cy, def.color, def.secondaryColor, bobOffset)
        break
      case 'snake':
        this.drawSnake(ctx, cx, cy, def.color, def.secondaryColor, frame)
        break
      case 'wolf':
        this.drawWolf(ctx, cx, cy, def.color, def.secondaryColor, bobOffset)
        break
    }

    ctx.restore()
  }

  private static drawRat(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    mainColor: string,
    secondaryColor: string,
    bobOffset: number
  ): void {
    // Body (oval)
    ctx.fillStyle = mainColor
    ctx.beginPath()
    ctx.ellipse(cx, cy + 2 - bobOffset / 2, 10, 7, 0, 0, Math.PI * 2)
    ctx.fill()

    // Belly
    ctx.fillStyle = secondaryColor
    ctx.beginPath()
    ctx.ellipse(cx, cy + 4 - bobOffset / 2, 6, 4, 0, 0, Math.PI * 2)
    ctx.fill()

    // Head
    ctx.fillStyle = mainColor
    ctx.beginPath()
    ctx.ellipse(cx + 8, cy - 2 - bobOffset / 2, 6, 5, 0.3, 0, Math.PI * 2)
    ctx.fill()

    // Ears
    ctx.beginPath()
    ctx.arc(cx + 10, cy - 8 - bobOffset / 2, 3, 0, Math.PI * 2)
    ctx.arc(cx + 6, cy - 8 - bobOffset / 2, 3, 0, Math.PI * 2)
    ctx.fill()

    // Eye
    ctx.fillStyle = '#000'
    ctx.beginPath()
    ctx.arc(cx + 11, cy - 3 - bobOffset / 2, 1.5, 0, Math.PI * 2)
    ctx.fill()

    // Tail
    ctx.strokeStyle = mainColor
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(cx - 10, cy + 2 - bobOffset / 2)
    ctx.quadraticCurveTo(cx - 16, cy - 4, cx - 14, cy + 6)
    ctx.stroke()

    // Whiskers
    ctx.strokeStyle = '#333'
    ctx.lineWidth = 0.5
    for (let i = 0; i < 3; i++) {
      ctx.beginPath()
      ctx.moveTo(cx + 12, cy - 1 - bobOffset / 2 + i * 2)
      ctx.lineTo(cx + 16, cy - 3 - bobOffset / 2 + i * 2)
      ctx.stroke()
    }
  }

  private static drawSnake(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    mainColor: string,
    secondaryColor: string,
    frame: number
  ): void {
    const waveOffset = Math.sin(frame * Math.PI / 2) * 3

    // Body segments (wavy)
    ctx.fillStyle = mainColor
    ctx.strokeStyle = secondaryColor
    ctx.lineWidth = 1

    // Draw coiled body
    ctx.beginPath()
    ctx.moveTo(cx - 12, cy + 6)
    ctx.quadraticCurveTo(cx - 6 + waveOffset, cy + 2, cx, cy + 6)
    ctx.quadraticCurveTo(cx + 6 - waveOffset, cy + 10, cx + 10, cy + 6)
    ctx.quadraticCurveTo(cx + 8, cy, cx + 4, cy - 4)
    ctx.lineTo(cx + 4, cy + 4)
    ctx.quadraticCurveTo(cx + 6, cy + 8, cx, cy + 4)
    ctx.quadraticCurveTo(cx - 4 + waveOffset, cy, cx - 10, cy + 4)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()

    // Head
    ctx.fillStyle = mainColor
    ctx.beginPath()
    ctx.ellipse(cx + 4, cy - 6, 5, 4, 0.2, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()

    // Eyes
    ctx.fillStyle = '#ff0'
    ctx.beginPath()
    ctx.arc(cx + 6, cy - 7, 1.5, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#000'
    ctx.beginPath()
    ctx.arc(cx + 6.5, cy - 7, 0.5, 0, Math.PI * 2)
    ctx.fill()

    // Tongue
    ctx.strokeStyle = '#ff6666'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(cx + 8, cy - 6)
    ctx.lineTo(cx + 12, cy - 7 + waveOffset / 2)
    ctx.moveTo(cx + 10, cy - 7 + waveOffset / 2)
    ctx.lineTo(cx + 12, cy - 5 + waveOffset / 2)
    ctx.stroke()

    // Scale pattern
    ctx.fillStyle = secondaryColor
    for (let i = 0; i < 4; i++) {
      ctx.beginPath()
      ctx.arc(cx - 8 + i * 5, cy + 5 + (i % 2) * 2, 2, 0, Math.PI * 2)
      ctx.fill()
    }
  }

  private static drawWolf(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    mainColor: string,
    secondaryColor: string,
    bobOffset: number
  ): void {
    // Body
    ctx.fillStyle = mainColor
    ctx.beginPath()
    ctx.ellipse(cx - 2, cy + 2 - bobOffset / 2, 12, 8, 0, 0, Math.PI * 2)
    ctx.fill()

    // Chest/belly
    ctx.fillStyle = secondaryColor
    ctx.beginPath()
    ctx.ellipse(cx + 4, cy + 4 - bobOffset / 2, 6, 5, 0, 0, Math.PI * 2)
    ctx.fill()

    // Back legs
    ctx.fillStyle = mainColor
    ctx.fillRect(cx - 10, cy + 4 - bobOffset / 2, 4, 10)
    ctx.fillRect(cx - 4, cy + 4 - bobOffset / 2, 4, 10 + Math.abs(bobOffset))

    // Front legs
    ctx.fillRect(cx + 4, cy + 4 - bobOffset / 2, 4, 10 + Math.abs(bobOffset))
    ctx.fillRect(cx + 10, cy + 4 - bobOffset / 2, 4, 10)

    // Paws
    ctx.fillStyle = secondaryColor
    ctx.fillRect(cx - 11, cy + 13 - bobOffset / 2, 6, 3)
    ctx.fillRect(cx - 5, cy + 13 + Math.abs(bobOffset) - bobOffset / 2, 6, 3)
    ctx.fillRect(cx + 3, cy + 13 + Math.abs(bobOffset) - bobOffset / 2, 6, 3)
    ctx.fillRect(cx + 9, cy + 13 - bobOffset / 2, 6, 3)

    // Tail
    ctx.fillStyle = mainColor
    ctx.beginPath()
    ctx.moveTo(cx - 14, cy - bobOffset / 2)
    ctx.quadraticCurveTo(cx - 20, cy - 8 - bobOffset, cx - 18, cy - 12 - bobOffset / 2)
    ctx.lineTo(cx - 14, cy - 4 - bobOffset / 2)
    ctx.closePath()
    ctx.fill()

    // Head
    ctx.fillStyle = mainColor
    ctx.beginPath()
    ctx.ellipse(cx + 12, cy - 4 - bobOffset / 2, 7, 6, 0.2, 0, Math.PI * 2)
    ctx.fill()

    // Snout
    ctx.fillStyle = secondaryColor
    ctx.beginPath()
    ctx.ellipse(cx + 18, cy - 3 - bobOffset / 2, 4, 3, 0.1, 0, Math.PI * 2)
    ctx.fill()

    // Ears
    ctx.fillStyle = mainColor
    ctx.beginPath()
    ctx.moveTo(cx + 8, cy - 10 - bobOffset / 2)
    ctx.lineTo(cx + 11, cy - 16 - bobOffset / 2)
    ctx.lineTo(cx + 14, cy - 10 - bobOffset / 2)
    ctx.closePath()
    ctx.fill()
    ctx.beginPath()
    ctx.moveTo(cx + 12, cy - 10 - bobOffset / 2)
    ctx.lineTo(cx + 15, cy - 16 - bobOffset / 2)
    ctx.lineTo(cx + 18, cy - 10 - bobOffset / 2)
    ctx.closePath()
    ctx.fill()

    // Eyes
    ctx.fillStyle = '#ffcc00'
    ctx.beginPath()
    ctx.arc(cx + 14, cy - 6 - bobOffset / 2, 2, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#000'
    ctx.beginPath()
    ctx.arc(cx + 14.5, cy - 6 - bobOffset / 2, 1, 0, Math.PI * 2)
    ctx.fill()

    // Nose
    ctx.fillStyle = '#333'
    ctx.beginPath()
    ctx.arc(cx + 21, cy - 3 - bobOffset / 2, 2, 0, Math.PI * 2)
    ctx.fill()
  }
}
